import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "../../styles/NavBar.css";
import Logout from "../Registration/LogOut";
import {
  getCurrentUser,
  isLoggedIn as checkLogin,
} from "../../utility/UserUtils";
import MegaMenu from "./MegaMenu";
import { IoChevronDown } from "react-icons/io5";

const NavBar = () => {
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [pillarsOpen, setPillarsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(checkLogin());
  const [user, setUser] = useState(getCurrentUser());

  useEffect(() => {
    const updateAuth = () => {
      setIsLoggedIn(checkLogin());
      setUser(getCurrentUser());
    };

    const resize = () => {
      if (window.innerWidth > 1024) {
        setMenuOpen(false);
        setPillarsOpen(false);
      }
    };

    window.addEventListener("authChange", updateAuth);
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("authChange", updateAuth);
      window.removeEventListener("resize", resize);
    };
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
    setPillarsOpen(false);
  };

  const linkClass = ({ isActive }) => "nav-link" + (isActive ? " active" : "");

  return (
    <nav className="bg-title">
      {/* Logo */}
      <div className="logo" onClick={() => handleNavigate("/")}>
        <img src="/assets/images/wiselogo.png" alt="WISE Logo" />
      </div>

      {/* Desktop Nav */}
      <div className="nav-links">
        <NavLink to="/" className={linkClass} end>
          Home
        </NavLink>

        <NavLink to="/about" className={linkClass}>
          About Us
        </NavLink>

        <NavLink to="/events" className={linkClass}>
          Events
        </NavLink>

        <MegaMenu
          title="Pillars"
          items={[
            {
              label: "Mentorship",
              to: "/mentorship",
              image: "/assets/projects/mentor.svg",
              heading: "Grow with Expert Mentorship",
              description:
                "Connect with experienced mentors who guide you through career and personal growth.",
              buttonLabel: "Explore Mentorship",
            },
            {
              label: "Training",
              to: "/training",
              image: "/assets/projects/training.svg",
              heading: "Hands-on Skill Training",
              description:
                "Upskill with practical training programs designed for real-world impact.",
              buttonLabel: "View Training",
            },
            {
              label: "Projects",
              to: "/projects",
              image: "/assets/projects/project.svg",
              heading: "Build Real Projects",
              description:
                "Turn ideas into impactful projects and collaborate with like-minded innovators.",
              buttonLabel: "Start a Project",
            },
            {
              label: "Storytelling",
              to: "/storytelling",
              image: "/assets/projects/storytelling.svg",
              heading: "Stories That Inspire",
              description:
                "Discover journeys, experiences, and ideas that spark inspiration and change.",
              buttonLabel: "Read Stories",
            },
          ]}
        />
        <NavLink to="/blogs" className={linkClass}>
          Blogs
        </NavLink>

        <NavLink to="/resources" className={linkClass}>
          Resources
        </NavLink>
        <NavLink to="/referral" className={linkClass}>
          Referral
        </NavLink>
        <NavLink to="/contactus" className={linkClass}>
          Contact Us
        </NavLink>
      </div>

      {/* Desktop Right Buttons */}
      <div className="right-buttons">
        {!isLoggedIn && (
          <NavLink to="/signup" className="nav-link signup-link">
            Sign Up
          </NavLink>
        )}

        {isLoggedIn ? (
          <div className="nav-profile-section">
            <img
              src={user?.avatarUrl || "/assets/images/default-avatar-v2.png"}
              alt="Profile"
              className="nav-avatar"
              onClick={() => handleNavigate("/profile")}
            />
            <Logout />
          </div>
        ) : (
          <button
            className="btn-login"
            onClick={() => handleNavigate("/login")}
          >
            Login
          </button>
        )}
      </div>

      {/* Hamburger */}
      <div
        className={`hamburger${menuOpen ? " open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <div />
        <div />
        <div />
      </div>

      {/* ================= MOBILE MENU ================= */}
      <div className={`mobile-menu${menuOpen ? " open" : ""}`}>
        <NavLink
          to="/"
          className="nav-link"
          onClick={() => handleNavigate("/")}
        >
          Home
        </NavLink>

        <NavLink
          to="/about"
          className="nav-link"
          onClick={() => handleNavigate("/about")}
        >
          About Us
        </NavLink>

        <NavLink
          to="/events"
          className="nav-link"
          onClick={() => handleNavigate("/events")}
        >
          Events
        </NavLink>

        {/* Pillars Accordion */}
        <div className="mobile-dropdown">
          <div
            className="nav-link mobile-dropdown-title"
            onClick={() => setPillarsOpen(!pillarsOpen)}
          >
            Pillars
            <IoChevronDown
              className={`dropdown-arrow ${pillarsOpen ? "open" : ""}`}
            />
          </div>

          {pillarsOpen && (
            <div className="mobile-submenu">
              <NavLink
                to="/mentorship"
                className="nav-link sub-link"
                onClick={() => handleNavigate("/mentorship")}
              >
                Mentorship
              </NavLink>

              <NavLink
                to="/training"
                className="nav-link sub-link"
                onClick={() => handleNavigate("/training")}
              >
                Training
              </NavLink>
              <NavLink
                to="/projects"
                className="nav-link sub-link"
                onClick={() => handleNavigate("/projects")}
              >
                Projects
              </NavLink>
              <NavLink
                to="/storytelling"
                className="nav-link sub-link"
                onClick={() => handleNavigate("/storytelling")}
              >
                Storytelling
              </NavLink>
            </div>
          )}
        </div>

        <NavLink
          to="/blogs"
          className="nav-link"
          onClick={() => handleNavigate("/blogs")}
        >
          Blogs
        </NavLink>

        <NavLink
          to="/resources"
          className="nav-link"
          onClick={() => handleNavigate("/resources")}
        >
          Resources
        </NavLink>

        <NavLink
          to="/contactus"
          className="nav-link"
          onClick={() => handleNavigate("/contactus")}
        >
          Contact Us
        </NavLink>

        {!isLoggedIn && (
          <NavLink
            to="/signup"
            className="nav-link signup-link"
            onClick={() => setMenuOpen(false)}
          >
            Sign Up
          </NavLink>
        )}

        {/*  MOBILE BOTTOM LEFT ACTIONS */}
        {isLoggedIn && (
          <div className="mobile-menu-actions">
            <div
              className="mobile-profile-avatar-wrapper"
              onClick={() => handleNavigate("/profile")}
            >
              {user?.avatar?.url ? (
                <img
                  src={
                    user?.avatarUrl || "/assets/images/default-avatar-v2.png"
                  }
                  alt="Profile"
                  className="nav-avatar"
                  onClick={() => handleNavigate("/profile")}
                />
              ) : (
                <div className="mobile-profile-placeholder">
                  {user?.firstName?.[0] || "U"}
                </div>
              )}
            </div>

            <div className="mobile-logout-btn">
              <Logout />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavBar;
